import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { captureAuthSession, isAuthSessionCurrent } from '../../lib/authSessionGuard';
import Modal from '../../components/modal/Modal';
import Table from '../../components/Table/Table';
import { createColumnHelper } from '@tanstack/react-table';
import { DemiLightText, MediumText, RegularText } from '../../lib/Text';
import { colors } from '../../lib/colors';
import { Col, getItemWithExpiration, setItemWithExpiration, isEmptyString, Row } from '../../lib/utils';
import SvgIcon from '../../components/SvgIcon';
import { cloneDeep } from 'lodash';
import dayjs from 'dayjs';
import useSingleQuery from '../../hook/useSingleQuery';
import { fetchSalesCommissionQuery, SalesCommission } from '../../services/useSalesCommissionQueryService';
import useSalesCommissionMutationService from '../../services/useSalesCommissionMutationService';
import { useQueryClient } from '@tanstack/react-query';
import DetailedCommissionTab, { DetailedCommissionTabRef } from './components/DetailedCommissionTab';
import SessionTierCommissionTab, { SessionTierCommissionTabRef } from './components/SessionTierCommissionTab';
import { overlay } from 'overlay-kit';
import ModalConfirmApply from './ModalConfirmApply';
import ModalChangeDetailSettlementMode from './ModalChangeDetailSettlementMode';
import ModalAlertOneButton from '../../components/modal/ModalAlertOneButton';
import { usePatchCenterSettlementType } from '../../features/centerSettlement/hooks';
import { SettlementType } from '../../features/centerSettlement/types';
import { useExceptionRatesEditor } from './components/exceptionRates/useExceptionRatesEditor';
import { AccessModeProvider } from '@/context/AccessModeContext';
import BaseExceptionRatesCell, { BaseExceptionEditorContext } from './components/exceptionRates/BaseExceptionRatesCell';

const columnHelper = createColumnHelper<SalesCommission>();

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    date: string;
    onWorkingHoursClick: () => void;
};

const ModalCommissionContent = ({ setIsVisible, date: _date, onWorkingHoursClick }: Props) => {
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const queryClient = useQueryClient();

    const { mutateAsync: patchCenterSettlementType } = usePatchCenterSettlementType();

    const tableRef = useRef<any>(null);
    const detailedRef = useRef<DetailedCommissionTabRef>(null);
    const sessionTierRef = useRef<SessionTierCommissionTabRef>(null);
    const [date, setDate] = useState(_date);

    const [selectedTab, setSelectedTab] = useState<SettlementType>(authObject.settlementType ?? 'base');

    // 버튼 비활성
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [isDetailedModified, setIsDetailedModified] = useState<boolean>(false);
    const [isSessionTierModified, setIsSessionTierModified] = useState<boolean>(false);

    const { patchSalesCommissionMutation } = useSalesCommissionMutationService({
        onPatchSalesCommissionSuccess() {
            queryClient.invalidateQueries({
                queryKey: ['getCenterSalesSettlementCalendarQuery'],
            });
            queryClient.invalidateQueries({
                queryKey: ['getClassFeeSettlement'],
            });
            queryClient.invalidateQueries({
                queryKey: ['getClassFeeSettlementForMembershipQuery'],
            });
            setIsVisible(false);
        },
    });

    //fetchSalesCommissionQuery
    const {
        refinedResult: singleResult,
        isLoading,
        refresh,
    } = useSingleQuery({
        fetchFn: fetchSalesCommissionQuery,
        params: {
            year: dayjs(date).year(),
            month: dayjs(date).month() + 1,
            centerId: authObject?.centerId,
            accessToken: authorization,
        },
        enabled: !!authObject.centerId,
        suspense: false,
        cacheTime: 0,
    });
    const _data = singleResult?.data as SalesCommission[];

    // singleResult 데이터가 로드될 때 업데이트
    useEffect(() => {
        if (_data) {
            setData(_data);
        }
    }, [_data]);

    // 데이터
    const [data, setData] = useState<SalesCommission[]>(_data);
    // 데이터 컬럼 설정
    const [columnVisibility, setColumnVisibility] = useState<any>({
        reRegisterCommission: false,
        noshowCommission: false,
    });

    /**
     * 기본 정산 탭의 예외 수업료 — 새 서버(exceptionRatesAllModes)일 때만 표 끝에 강사별 칸을 붙인다.
     * 비율 표의 「적용」(PATCH)과 따로, 칸을 눌러 강사 1명씩 저장한다. 다른 탭이면 조회하지 않는다.
     */
    const baseExceptionEditor = useExceptionRatesEditor({
        centerId: authObject?.centerId,
        year: dayjs(date).year().toString(),
        month: (dayjs(date).month() + 1).toString(),
        settlementMode: 'base',
        enabled: selectedTab === 'base',
        // 서버 값 기준(아직 적용 안 한 표 입력은 보지 않는다). commissionType null = 이 달 기본 비율 없음.
        baseRateMissing: (coachId: string) =>
            !!_data && _data.some((row) => row.coachId === coachId && row.commissionType === null),
    });

    /**
     * 비율 입력 칸은 입력값을 셀 안 상태에 두고 blur 때만 data 에 옮긴다. 그래서 이 열 정의(셀 함수)가 새로 만들어지면
     * 셀이 다시 마운트되어 blur 전 입력이 사라진다. 예외 수업료 응답(목록·분류)이 늦게 와도 이 열들은 다시 만들지 않는다 —
     * 예외 칸은 아래 exceptionColumn 으로 따로 붙이고, 편집기 값은 컨텍스트로 읽는다.
     */
    const inputColumns = useMemo(() => {
        return [
            columnHelper.accessor('coachName', {
                id: 'coachName',
                header: '강사',
                cell: (info) => info.getValue(),
                size: 300,
                enableSorting: false,
            }),
            columnHelper.accessor('commissionType', {
                id: 'commissionType',
                header: '정률제/정액제 선택',
                cell: (info) => {
                    const value = info?.getValue() === null ? 'percentage' : info?.getValue();
                    return (
                        <div className="flex items-center justify-center">
                            <ToggleSwitch
                                value={value === 'percentage' ? 'percentage' : 'fixed'}
                                onToggle={(value) =>
                                    setData((prev) => {
                                        const newData = cloneDeep(prev);
                                        newData[info.row.index].commissionType =
                                            value === 'percentage' ? 'percentage' : 'fixed';
                                        newData[info.row.index].scheduleCommission = '0';
                                        newData[info.row.index].reRegisterCommission = '0';
                                        newData[info.row.index].noshowCommission = '0';
                                        return newData;
                                    })
                                }
                            />
                        </div>
                    );
                },
                size: 300,
                enableSorting: false,
            }),
            // 신규 커미션
            columnHelper.accessor('scheduleCommission', {
                id: 'scheduleCommission',

                header: !!columnVisibility['reRegisterCommission'] ? '신규' : '수업 정산액',
                // header: '신규',
                cell: (info) => {
                    // commissionType 이 null 인경우 '' 으로 변경
                    const initialValue = info.row.original.commissionType === null ? '' : info.getValue();
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    const [value, setValue] = useState(initialValue);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        setValue(initialValue);
                    }, [initialValue]);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    // useEffect(() => {
                    //     setValue(initialValue);
                    // }, [info.row.original.commissionType]);

                    return (
                        <TextInput
                            value={value}
                            onChange={(value) => setValue(value as any)}
                            // unit={selected === 'Percentage' ? '%' : '원'}
                            unit={
                                info.row.original.commissionType === null
                                    ? '%'
                                    : info.row.original.commissionType === 'percentage'
                                      ? '%'
                                      : '원'
                            }
                            width={80}
                            type={info.row.original.commissionType}
                            onBlur={() =>
                                setData((prev) => {
                                    const newData = [...prev];
                                    newData[info.row.index].scheduleCommission = value;
                                    return newData;
                                })
                            }
                        />
                    );
                },
                size: 300,
                enableSorting: false,
            }),
            // 재결제 커미션
            columnHelper.accessor('reRegisterCommission', {
                id: 'reRegisterCommission',
                header: '재결제',
                cell: (info) => {
                    const initialValue = info.getValue();
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    const [value, setValue] = useState(initialValue);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        setValue(initialValue);
                    }, [initialValue]);

                    return (
                        <TextInput
                            value={value ?? ''}
                            onChange={(value) => setValue(value as any)}
                            // unit={selected === 'Percentage' ? '%' : '원'}
                            unit={info.row.original.commissionType === 'percentage' ? '%' : '원'}
                            width={80}
                            type={info.row.original.commissionType}
                            onBlur={() =>
                                setData((prev) => {
                                    const newData = [...prev];
                                    newData[info.row.index].reRegisterCommission = value;
                                    return newData;
                                })
                            }
                        />
                    );
                },
                size: 300,
                enableSorting: false,
            }),
            // 노쇼 커미션
            columnHelper.accessor('noshowCommission', {
                id: 'noshowCommission',
                header: '노쇼',
                cell: (info) => {
                    const initialValue = info.getValue();
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    const [value, setValue] = useState(initialValue);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        setValue(initialValue);
                    }, [initialValue]);

                    return (
                        <TextInput
                            value={value as any}
                            onChange={(value) => setValue(value as any)}
                            // unit={selected === 'Percentage' ? '%' : '원'}
                            unit={info.row.original.commissionType === 'percentage' ? '%' : '원'}
                            width={80}
                            type={info.row.original.commissionType}
                            onBlur={() =>
                                setData((prev) => {
                                    const newData = [...prev];
                                    newData[info.row.index].noshowCommission = value;
                                    return newData;
                                })
                            }
                        />
                    );
                },
                size: 300,
                enableSorting: false,
            }),
        ];
    }, [columnVisibility]);

    // 예외 수업료 칸 — 정의는 한 번만 만든다(편집기 값이 바뀌어도 열을 다시 만들지 않는다).
    const exceptionColumn = useMemo(
        () =>
            columnHelper.display({
                id: 'exceptionRates',
                header: '예외 수업료',
                cell: (info) => (
                    <div className="flex items-center justify-center">
                        <BaseExceptionRatesCell coachId={info.row.original.coachId} />
                    </div>
                ),
                size: 160,
                enableSorting: false,
            }),
        [],
    );
    // 붙였다 뗐다 해도 입력 칸의 셀 함수는 그대로라 다시 마운트되지 않는다.
    const columns = useMemo(
        () => (baseExceptionEditor.visible ? [...inputColumns, exceptionColumn] : inputColumns),
        [inputColumns, exceptionColumn, baseExceptionEditor.visible],
    );

    // 처음 데이터에서 재결제 데이터가 하나라도 있으면 columnVisibility['reRegisterCommission'] = true
    useEffect(() => {
        if (_data) {
            const isReRegisterCommission = _data?.some((item) => !isEmptyString(item.reRegisterCommission));
            const isNoshowCommission = _data?.some((item) => !isEmptyString(item.noshowCommission));
            setColumnVisibility({
                reRegisterCommission: isReRegisterCommission,
                noshowCommission: isNoshowCommission,
            });
        }
    }, [_data]);

    const toggleColumnVisibility = (columnId: string) => {
        setColumnVisibility((prev: any) => {
            // 신규/재결제 별도 설정 버튼 true -> false 할때 재결제 컬럼 값들 모두 초기화
            if (columnId === 'reRegisterCommission' && prev['reRegisterCommission']) {
                setData((prev) => {
                    const newData = cloneDeep(prev);
                    newData.forEach((item) => {
                        item.reRegisterCommission = undefined;
                    });
                    return newData;
                });
            }
            if (columnId === 'noshowCommission' && prev['noshowCommission']) {
                setData((prev) => {
                    const newData = cloneDeep(prev);
                    newData.forEach((item) => {
                        item.noshowCommission = undefined;
                    });
                    return newData;
                });
            }
            return {
                ...prev,
                [columnId]: !prev[columnId],
            };
        });
    };

    // 렌더링 시 배열로 변환
    const visibleColumnsArray = Object.entries(columnVisibility).map(([id, visible]) => ({
        id,
        visible,
    }));
    useEffect(() => {
        if (!_data) return;
        let isDisabled = false;

        for (let i = 0; i < data.length; i++) {
            const row = data[i];

            if (columnVisibility['reRegisterCommission'] && columnVisibility['noshowCommission']) {
                if (
                    !isEmptyString(row.scheduleCommission) &&
                    !isEmptyString(row.reRegisterCommission) &&
                    !isEmptyString(row.noshowCommission)
                ) {
                    isDisabled = false;
                } else {
                    isDisabled = true;
                    break;
                }
            } else if (columnVisibility['reRegisterCommission']) {
                if (!isEmptyString(row.scheduleCommission) && !isEmptyString(row.reRegisterCommission)) {
                    isDisabled = false;
                } else {
                    isDisabled = true;
                    break;
                }
            } else if (columnVisibility['noshowCommission']) {
                if (!isEmptyString(row.scheduleCommission) && !isEmptyString(row.noshowCommission)) {
                    isDisabled = false;
                } else {
                    isDisabled = true;
                    break;
                }
            } else {
                if (!isEmptyString(row?.scheduleCommission)) {
                    isDisabled = false;
                } else {
                    isDisabled = true;
                    break;
                }
            }
        }

        setIsDisabled(isDisabled);
    }, [data, columnVisibility]);

    return (
        // <Modal whiteViewWidth={1000} setIsVisible={setIsVisible} isCloseOutside borderRadius={40} modalAlign="start">
        <Modal setIsVisible={setIsVisible} isCloseOutside={false} borderRadius={37} modalAlign="center">
            <div className="flex flex-col w-[920px] h-full  px-[35px] py-[36px] bg-white rounded-[40px]">
                <Row className="flex w-full items-center mb-[32px] justify-between">
                    <Row className="flex w-full items-center">
                        <div
                            className="cursor-pointer mr-[20px]"
                            onClick={() => setDate(dayjs(date).subtract(1, 'month').toISOString())}
                        >
                            <SvgIcon
                                name="SvgLeftStrokeArrow"
                                size={18}
                                fill={colors.trans}
                                stroke="#808B96"
                                strokeWidth={2}
                            />
                        </div>
                        <MediumText
                            fontSize={22}
                            color="#181D24"
                            letterSpacing={-0.5}
                            className="w-[120px] text-center"
                        >
                            {dayjs(date).format('YYYY년 M월')}
                        </MediumText>
                        <div
                            className="cursor-pointer ml-[20px] mr-[30px]"
                            onClick={() => setDate(dayjs(date).add(1, 'month').toISOString())}
                        >
                            <SvgIcon
                                name="SvgRightStrokeArrow"
                                size={18}
                                fill={colors.trans}
                                stroke="#808B96"
                                strokeWidth={2}
                            />
                        </div>
                        <MediumText fontSize={22} color="#3D4244" className="mr-[50px]">
                            월별 정산액 비율 설정
                        </MediumText>
                    </Row>
                    {authObject.settlementType === 'tier' && (
                        <Row
                            className="bg-[#2D3132] rounded-[10px] justify-center items-center flex-row flex w-[250px] h-[44px] cursor-pointer "
                            onClick={onWorkingHoursClick}
                        >
                            <SvgIcon name={'SvgCoachCommissonSetting'} size={17} fill={'white'} className="mr-[9px]" />
                            <RegularText fontSize={16} lineHeight={23} letterSpacing={-0.3} color="white">
                                강사별 근무시간 설정
                            </RegularText>
                        </Row>
                    )}
                </Row>

                {/* 탭 토글 */}
                <Row className="mb-[24px] flex w-full justify-between ">
                    <div className="flex bg-[#F4F5F7] rounded-full p-[4px] w-[468px] h-[58px]">
                        <div
                            className={`flex flex-1 items-center justify-center rounded-full cursor-pointer transition-colors ${
                                selectedTab === 'base' ? 'bg-white shadow-sm' : ''
                            }`}
                            onClick={() => {
                                if (selectedTab !== 'base') {
                                    overlay.open(({ unmount }) => (
                                        <ModalChangeDetailSettlementMode
                                            onClose={unmount}
                                            onConfirm={async () => {
                                                // 늦은 응답이 다른 세션 저장소를 덮지 않게 요청 전에 세션 표식을 잡는다.
                                                const authSession = captureAuthSession(authObject);
                                                if (authObject?.centerId) {
                                                    try {
                                                        await patchCenterSettlementType({
                                                            centerId: authObject.centerId,
                                                            body: { settlementType: 'base' },
                                                        });
                                                    } catch (error) {
                                                        console.error(error);
                                                    }
                                                }
                                                setSelectedTab('base');
                                                const newAuthObject = { ...authObject, settlementType: 'base' };
                                                if (!isAuthSessionCurrent(authSession)) return;
                                                setItemWithExpiration('authObject', newAuthObject, 12);
                                                setAuthObject(newAuthObject);
                                            }}
                                        />
                                    ));
                                }
                            }}
                        >
                            <MediumText fontSize={20} color={selectedTab === 'base' ? '#3D4244' : '#6B7280'}>
                                기본 정산
                            </MediumText>
                        </div>
                        <div
                            className={`flex flex-1 items-center justify-center rounded-full cursor-pointer transition-colors ${
                                selectedTab === 'tier' ? 'bg-white shadow-sm' : ''
                            }`}
                            onClick={() => {
                                if (selectedTab !== 'tier') {
                                    overlay.open(({ unmount }) => (
                                        <ModalChangeDetailSettlementMode
                                            onClose={unmount}
                                            onConfirm={async () => {
                                                // 늦은 응답이 다른 세션 저장소를 덮지 않게 요청 전에 세션 표식을 잡는다.
                                                const authSession = captureAuthSession(authObject);
                                                if (authObject?.centerId) {
                                                    try {
                                                        await patchCenterSettlementType({
                                                            centerId: authObject.centerId,
                                                            body: { settlementType: 'tier' },
                                                        });
                                                    } catch (error) {
                                                        console.error(error);
                                                    }
                                                }
                                                setSelectedTab('tier');
                                                const newAuthObject = { ...authObject, settlementType: 'tier' };
                                                if (!isAuthSessionCurrent(authSession)) return;
                                                setItemWithExpiration('authObject', newAuthObject, 12);
                                                setAuthObject(newAuthObject);
                                            }}
                                        />
                                    ));
                                }
                            }}
                        >
                            <MediumText fontSize={20} color={selectedTab === 'tier' ? '#3D4244' : '#6B7280'}>
                                상세 정산
                            </MediumText>
                        </div>
                        <div
                            className={`flex flex-1 items-center justify-center rounded-full cursor-pointer transition-colors ${
                                selectedTab === 'sessionTier' ? 'bg-white shadow-sm' : ''
                            }`}
                            onClick={() => {
                                if (selectedTab !== 'sessionTier') {
                                    overlay.open(({ unmount }) => (
                                        <ModalChangeDetailSettlementMode
                                            onClose={unmount}
                                            title="세션별 정산으로 변경할까요?"
                                            onConfirm={async () => {
                                                // 늦은 응답이 다른 세션 저장소를 덮지 않게 요청 전에 세션 표식을 잡는다.
                                                const authSession = captureAuthSession(authObject);
                                                if (authObject?.centerId) {
                                                    try {
                                                        await patchCenterSettlementType({
                                                            centerId: authObject.centerId,
                                                            body: { settlementType: 'sessionTier' },
                                                        });
                                                    } catch (error) {
                                                        console.error(error);
                                                    }
                                                }
                                                setSelectedTab('sessionTier');
                                                const newAuthObject = {
                                                    ...authObject,
                                                    settlementType: 'sessionTier',
                                                };
                                                if (!isAuthSessionCurrent(authSession)) return;
                                                setItemWithExpiration('authObject', newAuthObject, 12);
                                                setAuthObject(newAuthObject);
                                            }}
                                        />
                                    ));
                                }
                            }}
                        >
                            <MediumText fontSize={20} color={selectedTab === 'sessionTier' ? '#3D4244' : '#6B7280'}>
                                세션별 정산
                            </MediumText>
                        </div>
                    </div>
                    {selectedTab === 'base' && (
                        <Row>
                            {
                                // coach, settlementType 제외
                                visibleColumnsArray.map((column: any, index: number) => {
                                    if (
                                        column.id === 'coach' ||
                                        column.id === 'commissionType' ||
                                        column.id === 'newCommissionPercentage'
                                    ) {
                                        return null;
                                    }
                                    const text =
                                        column.id === 'reRegisterCommission'
                                            ? '신규/재결제 별도 설정'
                                            : column.id === 'noshowCommission'
                                              ? '노쇼 설정'
                                              : column.id;
                                    return (
                                        <Row
                                            key={column.id}
                                            className="px-1 items-center"
                                            onClick={() => toggleColumnVisibility(column.id)}
                                        >
                                            <label
                                                className={`relative inline-flex items-center cursor-pointer ${
                                                    index === 0 ? 'mr-[24px]' : ''
                                                }`}
                                            >
                                                <div
                                                    className={`mr-[8px] border-[1px] border-primary border-solid rounded-md flex items-center justify-center ${
                                                        columnVisibility[column.id] ? 'bg-primary' : 'bg-white'
                                                    }`}
                                                    style={{
                                                        width: 24,
                                                        height: 24,
                                                    }}
                                                >
                                                    {columnVisibility[column.id] ? (
                                                        <svg
                                                            className=" text-white"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            viewBox="0 0 24 24"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                d="M5 11L10.6667 16.6666L20 6.66663"
                                                                stroke="white"
                                                            />
                                                        </svg>
                                                    ) : (
                                                        <div className="w-[15px] h-[15px]" />
                                                    )}
                                                </div>
                                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                                    {text}
                                                </RegularText>
                                            </label>
                                        </Row>
                                    );
                                })
                            }
                        </Row>
                    )}
                </Row>

                <Col
                    className="flex flex-col h-[480px] overflow-y-scroll
                scrollbar-thumb-[#B4B4B6] scrollbar-track-gray-200 scrollbar scrollbar-w-[10px]  scrollbar-track-rounded-full scrollbar-thumb-rounded-full scrollbar-h-[10px] 
                "
                >
                    {selectedTab === 'base' &&
                        !isLoading &&
                        data &&
                        data?.length > 0 && (
                            <BaseExceptionEditorContext.Provider value={baseExceptionEditor}>
                                <Table
                                    ref={tableRef}
                                    data={data}
                                    columns={columns}
                                    width={'100%'}
                                    height={403}
                                    isPagination={false}
                                    isColumnVisibility
                                    columnVisibility={columnVisibility}
                                    setColumnVisibility={setColumnVisibility}
                                />
                            </BaseExceptionEditorContext.Provider>
                        )}
                    {selectedTab === 'tier' && (
                        <DetailedCommissionTab
                            ref={detailedRef}
                            centerId={authObject?.centerId}
                            year={dayjs(date).year().toString()}
                            month={(dayjs(date).month() + 1).toString()}
                            onIsModifiedChange={setIsDetailedModified}
                        />
                    )}
                    {baseExceptionEditor.dialogs}
                    {selectedTab === 'sessionTier' && (
                        <SessionTierCommissionTab
                            ref={sessionTierRef}
                            centerId={authObject?.centerId}
                            year={dayjs(date).year().toString()}
                            month={dayjs(date).month() + 1 < 10
                                ? `0${dayjs(date).month() + 1}`
                                : `${dayjs(date).month() + 1}`}
                            onIsModifiedChange={setIsSessionTierModified}
                        />
                    )}
                </Col>
                <Row className="  self-end relative pt-[20px]">
                    {selectedTab === 'base' && isDisabled && (
                        <Col className="flex absolute  top-[-60px] ">
                            <Col className="py-[11px] px-[21px]  bg-[#36393E] rounded-[9px] cursor-pointer">
                                <RegularText fontSize={14} lineHeight={24} color="white" letterSpacing={-0.32}>
                                    모든 강사의 정산액 비율을 입력해주세요!
                                </RegularText>
                            </Col>
                            <div className=" mr-[30px] bg-[#00ff0000] border-r-[#36393E] self-end border-l-trans w-[10px] h-[10px] border-solid border-l-[30px] border-b-[10px] border-t-[0px] border-r-[10px] border-b-[#00ff0000]  border-l-transparent  border-t-[#00ff0000]  " />
                        </Col>
                    )}
                    {(selectedTab === 'tier' || selectedTab === 'sessionTier') && (
                        <Col
                            className="flex items-center justify-center px-[24px] h-[48px] border-[1px] border-solid border-[#D9D9D9] bg-white rounded-[9px] mr-[13px] cursor-pointer hover:bg-[#F2F4F6] transition-colors"
                            onClick={() => {
                                if (selectedTab === 'tier') {
                                    detailedRef.current?.handleApplyToAll();
                                } else {
                                    sessionTierRef.current?.handleApplyToAll();
                                }
                            }}
                        >
                            <RegularText fontSize={18} lineHeight={24} color="#3D4244" letterSpacing={-0.7}>
                                현재 정산비율 모든 강사에게 일괄 적용
                            </RegularText>
                        </Col>
                    )}
                    <Col
                        className=" items-center justify-center w-[150px] h-[48px] bg-[#F4F5F7] rounded-[9px] mr-[13px] cursor-pointer"
                        onClick={() => setIsVisible(false)}
                    >
                        <RegularText fontSize={20} lineHeight={24} color="#3D4244">
                            취소
                        </RegularText>
                    </Col>
                    <Col
                        className={` items-center justify-center w-[150px] h-[48px]  rounded-[9px] bg-primary ${
                            (selectedTab === 'base' && isDisabled) ||
                            (selectedTab === 'tier' && !isDetailedModified) ||
                            (selectedTab === 'sessionTier' && !isSessionTierModified)
                                ? ' bg-opacity-[30%] cursor-not-allowed '
                                : 'cursor-pointer '
                        }

                        `}
                        onClick={async () => {
                            if (selectedTab === 'base') {
                                if (isDisabled) {
                                    return;
                                }
                                overlay.open(({ unmount }) => (
                                    <ModalConfirmApply
                                        onClose={unmount}
                                        onConfirm={async () => {
                                            // comissionType 이 null 인경우 'percentage' 으로 변경
                                            const newData = data.map((item) => {
                                                item.commissionType = item.commissionType ?? 'percentage';
                                                return item;
                                            });

                                            await patchSalesCommissionMutation.mutateAsync({
                                                year: dayjs(date).year().toString(),
                                                month: (dayjs(date).month() + 1).toString(),
                                                salesCommissionArray: newData,
                                            });
                                            refresh();
                                        }}
                                    />
                                ));
                            } else if (selectedTab === 'tier') {
                                if (!isDetailedModified) return;
                                if (detailedRef.current) {
                                    overlay.open(({ unmount }) => (
                                        <ModalConfirmApply
                                            onClose={unmount}
                                            onConfirm={async () => {
                                                await detailedRef.current!.handleApply();
                                                queryClient.invalidateQueries({
                                                    queryKey: ['getCenterSalesSettlementCalendarQuery'],
                                                });
                                                queryClient.invalidateQueries({
                                                    queryKey: ['getClassFeeSettlement'],
                                                });
                                                queryClient.invalidateQueries({
                                                    queryKey: ['getClassFeeSettlementForMembershipQuery'],
                                                });
                                                setIsVisible(false); // 상세 정산 저장 후 닫기
                                            }}
                                        />
                                    ));
                                }
                            } else {
                                if (!isSessionTierModified) return;
                                if (sessionTierRef.current) {
                                    const validation = sessionTierRef.current.validate();
                                    if (!validation.valid) {
                                        overlay.open(({ unmount }) => (
                                            <ModalAlertOneButton
                                                title="구간 설정을 확인해주세요"
                                                content={validation.message ?? '구간 값이 유효하지 않습니다.'}
                                                isError
                                                isTwoButton={false}
                                                twoButtonContent={null}
                                                twoButtonCallBack={null}
                                                alertStateReset={unmount}
                                            />
                                        ));
                                        return;
                                    }
                                    overlay.open(({ unmount }) => (
                                        <ModalConfirmApply
                                            onClose={unmount}
                                            onConfirm={async () => {
                                                await sessionTierRef.current!.handleApply();
                                                queryClient.invalidateQueries({
                                                    queryKey: ['getCenterSalesSettlementCalendarQuery'],
                                                });
                                                queryClient.invalidateQueries({
                                                    queryKey: ['getClassFeeSettlement'],
                                                });
                                                queryClient.invalidateQueries({
                                                    queryKey: ['getClassFeeSettlementForMembershipQuery'],
                                                });
                                                setIsVisible(false);
                                            }}
                                        />
                                    ));
                                }
                            }
                        }}
                    >
                        <RegularText fontSize={20} lineHeight={24} color="white">
                            적용하기
                        </RegularText>
                    </Col>
                </Row>
            </div>
        </Modal>
    );
};

/**
 * 이 창은 overlay-kit 으로 열린다 — OverlayProvider 가 앱의 AccessModeProvider 밖에 있어 그대로 두면 열람 모드에서도 기본값(관리)을 읽는다.
 * 그래서 창 안에서 같은 저장소 값으로 권한 Provider 를 다시 씌운다(앱과 같은 판정, DOM 은 늘지 않는다).
 */
const ModalCommission = (props: Props) => {
    const [authObject] = useState(() => getItemWithExpiration('authObject'));
    return (
        <AccessModeProvider authObject={authObject}>
            <ModalCommissionContent {...props} />
        </AccessModeProvider>
    );
};

export default ModalCommission;

type ToggleProps = {
    value: 'percentage' | 'fixed';
    onToggle: (value: 'percentage' | 'fixed') => void;
};

export const ToggleSwitch: React.FC<ToggleProps> = ({ value, onToggle }) => {
    const [selected, setSelected] = useState<'percentage' | 'fixed'>(value);

    const handleToggle = (value: 'percentage' | 'fixed') => {
        setSelected(value);
        onToggle(value);
    };

    useEffect(() => {
        setSelected(value);
    }, [value]);

    return (
        <div className="relative flex items-center  rounded-full w-[120px] h-[36px] bg-[#F4F5F7] overflow-hidden border border-solid border-[#E2E2E2]">
            {/* Animated slider */}
            <div
                className={`absolute w-[50%] h-[28px] bg-white rounded-full border border-solid border-[#E2E2E2] transition-transform duration-300 ease-in-out ${
                    selected === 'percentage' ? 'translate-x-[4px]' : 'translate-x-[56px]'
                }`}
            ></div>

            {/* percentage button */}
            <button
                className={`relative flex-1 text-center text-[13px] font-medium z-10 transition-colors duration-300 ${
                    selected === 'percentage' ? 'text-blue-500' : 'text-gray-500'
                }`}
                onClick={() => handleToggle('percentage')}
            >
                <MediumText
                    fontSize={16}
                    lineHeight={24}
                    color={selected === 'percentage' ? colors.primary : '#3D4244'}
                >
                    %
                </MediumText>
            </button>

            {/* Flat button */}
            <button
                className={`relative flex-1 text-center text-[13px] font-medium z-10 transition-colors duration-300 ${
                    selected === 'fixed' ? 'text-gray-700' : 'text-gray-500'
                }`}
                onClick={() => handleToggle('fixed')}
            >
                <MediumText fontSize={16} lineHeight={24} color={selected === 'fixed' ? colors.primary : '#3D4244'}>
                    원
                </MediumText>
            </button>
        </div>
    );
};

type TextInputProps = {
    value: string | number;
    onChange: (value: string | number) => void;
    unit?: string; // 단위 (예: %, 원)
    placeholder?: string; // 플레이스홀더 텍스트
    width?: number; // 입력 필드 너비
    disabled?: boolean; // 비활성화 여부
    type: 'fixed' | 'percentage';
    onBlur?: () => void;
};

const TextInput: React.FC<TextInputProps> = ({
    value,
    onChange,
    unit = '',
    placeholder = '',
    width = 116,
    disabled = false,
    type,
    onBlur,
}) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    // 포커스
    const [isFocused, setIsFocused] = useState(false);
    //
    return (
        <div
            className={`flex items-center border  border-solid h-[46px] rounded-[10px] bg-white px-[18px] cursor-text hover:border-primary transition-colors duration-300 ease-in-out ${
                disabled ? 'bg-gray-100 text-gray-400' : 'bg-white'
            } ${isFocused ? 'border-primary' : 'border-[#D9D9D9B2]'}`}
            onClick={() => {
                inputRef.current?.focus();
            }}
        >
            <input
                ref={inputRef}
                type="text"
                value={value ?? ''}
                onChange={(e) => {
                    // 숫자입력시 0으로 시작하는 경우 0 제거
                    if (e.target.value.length > 1 && e.target.value[0] === '0') {
                        onChange(e.target.value.slice(1));
                        return;
                    }

                    // type === percentage 일때 100 이상 입력 방지
                    if (type === 'percentage' && Number(e.target.value) > 100) {
                        return;
                    }

                    // setText(e.target.value);
                    onChange(e.target.value);
                }}
                placeholder={placeholder}
                className={`flex flex-grow outline-none w-[80px] ${disabled ? 'cursor-not-allowed' : ''}`}
                // onFocus={() => setIsFocused(true)}
                // onBlur={() => {
                //     // onChange(text);
                //     setIsFocused(false);
                // }}
                onBlur={onBlur}
                disabled={disabled}
                maxLength={type === 'percentage' ? 3 : 7}
                // 숫자만
            />
            {unit && <DemiLightText className="flex">{unit}</DemiLightText>}
        </div>
    );
};
